import { MaterialInput } from 'app/components'
import { GenderSelector } from 'app/components/GenderSelector';
import { Container } from 'app/containers/Container'
import { cloneRetirementPerson, Gender, hasDOB, IRetirementPerson, isValidPerson, Profile, updateLifeExpectancy } from 'app/redux/Profile/model'
import {determineGender } from 'app/services/GenderDetermination'
import { LifeExpectancyService } from 'app/services/LifeExpectancyService';
import { MiscService } from 'app/services/MiscService'
import * as utils from 'app/utils'
import { isGoodMoment } from 'app/utils/DateCalcs';
import { getBrandInfo } from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Button, Col, FormGroup, Label, Modal, Row, Well } from 'react-bootstrap'

interface IProfileState {
  profile: Profile
  member: IRetirementPerson
  spouse: IRetirementPerson
  btnText: string
  isSaving: boolean
  redirectTo: string
  requiredPIA: boolean
  showErrors: boolean
  acceptancesError: string
  showVideo1: boolean
}

type Updater = <T extends keyof IRetirementPerson>(attr: T, value: IRetirementPerson[T]) => void

const minAge = 52
const maxAge = 110

function reasonableDate(when: moment.Moment): boolean {
  const r = (when.year() >= 1930) && (when.year() < 2025)
  if (!r) {
    // tslint:disable-next-line:no-console
    console.log('not a reasonableDate: ' + when.toISOString())
  }
  return r
}

const formatDate2 = (when: moment.Moment) => {
  if (!when) { return 'n/a' }
  const fmt = (utils.todaysDate().isSame(when.clone().startOf('day'))) ? 'LT' : 'l'
  return when.clone().local().format(fmt)
}

const guessGenderbasedOnFirstName = _.debounce((firstName: string, onDone: (g: Gender) => void) => {
  determineGender(firstName)
  .then(onDone)
}, 1000)

class ProfileSettings extends Container<{}, IProfileState> {
  state: IProfileState = {
    profile: null,
    member: null,
    spouse: null,
    isSaving: false,
    redirectTo: null,
    btnText: null,
    requiredPIA: false,
    showErrors: false,
    acceptancesError: '',
    showVideo1: false
  }

  minPIA = 41
  maxPIA = 10000
  minSocialSecurityAge = 62
  formElement: HTMLFormElement
  tosElement: HTMLInputElement
  ppElement: HTMLInputElement
  memberGenderHasChanged: boolean
  spouseGenderHasChanged: boolean

  protected  humanDebouncer = _.debounce((p: Profile) => this.Actions.Profile.store(p), 1100)

  componentWillMount() {
    this.Actions.Nav.showDefaultNavRoutes()
    this.state.redirectTo = this.getHashParamValue('redirectTo')
    this.state.btnText = this.getHashParamValue('btnText') || 'Done'
    this.state.requiredPIA = this.hasHashParam('requirePIA')
    this.memberGenderHasChanged = this.spouseGenderHasChanged = false

    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info')
      if (!profile) { return }
      // tslint:disable-next-line:no-console
      console.log('ProfileSetting- appStateUpdate')
      const member = this.state.member ? this.state.member : _.merge({}, profile.getHeadOfHousehold())
      const spouse = this.state.spouse ? this.state.spouse : _.merge({}, profile.getSpouse())
      const op = appState.Profile.get('op')
      const isSaving = op.isBusy

      this.setState({ profile, member, spouse, isSaving })
    })
  }

  onAcceptPP() {
    this.state.profile.privacyPolicyAcceptance.isAccepted = !this.state.profile.privacyPolicyAcceptance.isAccepted
    if (this.state.profile.privacyPolicyAcceptance.isAccepted) {
      this.state.profile.privacyPolicyAcceptance.when = moment.utc()
    }
    this.agreementsAreAccepted()
  }

  onAcceptTOS() {
    this.state.profile.termsOfServiceAcceptance.isAccepted = !this.state.profile.termsOfServiceAcceptance.isAccepted
    if (this.state.profile.termsOfServiceAcceptance.isAccepted) {
      this.state.profile.termsOfServiceAcceptance.when = moment.utc()
    }
    this.agreementsAreAccepted()
  }

  agreementsAreAccepted() {

    this.setState({ acceptancesError: '' })

    // RA-130 track TOS and PP acceptances
    const missingAcceptances = []

    const ppAccepted = this.state.profile.privacyPolicyAcceptance.isAccepted
    if (ppAccepted) {
      this.ppElement.setCustomValidity('')
    } else {
      this.ppElement.setCustomValidity('Must accept privacy policy')
      missingAcceptances.push('privacy policy')
    }

    const tosAccepted = this.state.profile.termsOfServiceAcceptance.isAccepted
    if (tosAccepted) {
      this.tosElement.setCustomValidity('')
    } else {
      this.tosElement.setCustomValidity('Must accept terms and conditions')
      missingAcceptances.push('terms of service')
    }

    if (0 !== missingAcceptances.length) {
      const acceptancesError = 'The ' + missingAcceptances.join(' & ') + ' agreement(s) still need to be accepted.'
      this.setState({ acceptancesError })
      return false
    }

    return true
  }

  isFormValid() {
    if (!this.formElement) {
      // tslint:disable-next-line:no-console
      console.log('onProfileUpdate- but NO form element!')
      return false
    }

    // ensure the form (all the data) is valid
    if (!this.formElement.checkValidity()) {
      // tslint:disable-next-line:no-console
      console.log('onProfileUpdate- form has invalid data')
      return false
    }

    return true
  }

  onProfileUpdate() {

    if (!this.isFormValid()) { return }

    // ok, the form is valid, so let's update the profile!

    // but first, let's check if the spouse dob was cleared-- 'cuz we have to clean it up
    const spouse = this.state.spouse
    if (spouse && !isGoodMoment(spouse.dob)) {
      this.state.profile.cleanUpPerson(spouse, false)
    }

    _.merge(this.state.profile.getHeadOfHousehold(), this.state.member)
    _.merge(this.state.profile.getSpouse(), this.state.spouse)

    this.humanDebouncer(this.state.profile)
  }

  updateLifeExpectancy<T extends keyof IRetirementPerson>(person: IRetirementPerson, attr: T, value: IRetirementPerson[T]): Promise<void> {

    // RA-127: changes to these fields cause a life expectancy lookup
    const needLifeExpectancyEvaluation = ['dob', 'selectedRetirementAge']
    if (_.includes(needLifeExpectancyEvaluation, attr)) {
      return updateLifeExpectancy(person)
      .then((p) => {
        // tslint:disable-next-line:no-console
        console.log('updated ' + p.firstName + ' born on ' + p.dob.toISOString() + ' life expectancy to ' + p.selectedAgeExpectancy)
        this.setState({ profile: this.state.profile })
      })
    }

    return Promise.resolve(undefined)
  }

  updateMember<T extends keyof IRetirementPerson>(attr: T, value: IRetirementPerson[T]) {
    // tslint:disable-next-line:no-console
    console.log('updateFirstMember- ' + attr + ' => value: ' + value)
    const member = cloneRetirementPerson(this.state.member)
    member[attr] = value
    this.setState({member})

    if ('firstName' === attr) {
      if (!this.memberGenderHasChanged) {
        // since the member gender has not been touched, go get a guesstimate based on firstName
        guessGenderbasedOnFirstName(member.firstName, (g) => {
          if (!_.isUndefined(g)) {
            this.updateMember('gender', g)
            this.memberGenderHasChanged = false //we didn't do it...
          }
        })
      }
    }

    if ('gender' === attr) {
      this.memberGenderHasChanged = true
    }

    this.agreementsAreAccepted()

    return this.updateLifeExpectancy(member, attr, value)
    .then(() => this.onProfileUpdate())
  }

  updateSpouse<T extends keyof IRetirementPerson>(attr: T, value: IRetirementPerson[T]) {
    // tslint:disable-next-line:no-console
    console.log('updateSecondMember- ' + attr + ' => value: ' + value)
    const spouse = cloneRetirementPerson(this.state.spouse)
    spouse[attr] = value
    this.setState({spouse})

    // if the spouse dob is cleared, we have to clean up the profile
    if ('dob' === attr) {
      if (!isGoodMoment(value as moment.Moment)) {
        this.state.profile.cleanUpPerson(spouse, false)
      }
    }

    if ('firstName' === attr) {
      if (!this.spouseGenderHasChanged) {
        // since the spouse gender has not been touched, go get a guesstimate based on firstName
        guessGenderbasedOnFirstName(spouse.firstName, (g) => {
          if (!_.isUndefined(g)) {
            this.updateSpouse('gender', g)
            this.spouseGenderHasChanged = false // we didn't do it...
          }
        })
      }
    }

    if ('gender' === attr) {
      this.spouseGenderHasChanged = true
    }

    this.agreementsAreAccepted()

    return this.updateLifeExpectancy(spouse, attr, value)
    .then(() => this.onProfileUpdate())
  }

  getMomentDateForInput(momentObj: moment.Moment) {
    if (moment.isMoment(momentObj)) {
      return momentObj.format('YYYY-MM-DD')
    }

    return ''
  }

  updateDateField(e: React.ChangeEvent<HTMLInputElement>, attr, updater: Updater) {
    const w = moment.utc(e.currentTarget.value)
    const goodDate = !w.isValid() || reasonableDate(w)
    if (goodDate) {
      // tslint:disable-next-line:no-console
      console.log('clearing error on date ' + e.currentTarget.value)
      e.currentTarget.setCustomValidity('')
      updater('dob', w)
    } else {
      // tslint:disable-next-line:no-console
      console.log('setting error on date ' + e.currentTarget.value)
      e.currentTarget.setCustomValidity('This is not a valid date')
    }
  }

  save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    if (!this.agreementsAreAccepted()) { return }

    this.Actions.Profile.store(this.state.profile).finally(() => {
      const showSnackbar = !this.state.redirectTo

      //Do not show save when submit button is being shown
      if (showSnackbar) {
        this.showSnackbar('Saved', 'success', 'fa fa-fw fa-thumbs-up')
      }

      if (this.state.redirectTo) {
        location.hash = this.state.redirectTo
      }
    })
  }

  renderForm() {
    const profile = this.state.profile
    if (!profile) { return null }

    const member = this.state.member
    // tslint:disable-next-line:no-console
    console.log('member gender: ' + member.gender)
    const spouse = this.state.spouse
    const spouseIsPresent = isValidPerson(spouse)
    // tslint:disable-next-line:no-console
    console.log('has second member: ' + spouseIsPresent)

    return <form
      ref={(e) => this.formElement = e}
      onSubmit={(e) => this.save(e)} className="container-question">
      <section>
        <header style={{ marginBottom: 27 }}>
          <h1 style={{ marginBottom: 5 }}>Profile Settings</h1>
          <Label>Created: {formatDate2(profile.createdWhen)}</Label>&nbsp;&nbsp;
          <Label>Modified: {formatDate2(profile.modifiedWhen)}</Label>&nbsp;&nbsp;
          <Label>Last login: {formatDate2(profile.lastLoginWhen)}</Label>&nbsp;&nbsp;
        </header>

        <h4>Acceptances</h4>
        <p>
          <label>
            <input type="checkbox" onChange={(e) => this.onAcceptTOS()}
              ref={(e) => this.tosElement = e}
              checked={this.state.profile.termsOfServiceAcceptance.isAccepted} />
              I accept the terms and conditions described <a target="_blank" href="/content/Retirement%20Calculator%20-%20Terms%20of%20Service%20-%20January%2019%202018.pdf">here</a>
          </label>
          <br/>
          <label>
            <input type="checkbox" onChange={(e) => this.onAcceptPP()}
              ref={(e) => this.ppElement = e}
              checked={this.state.profile.privacyPolicyAcceptance.isAccepted} />
              I accept the privacy policy described <a target="_blank" href="/content/Retirement%20Calculator%20-%20Privacy%20Policy%20-%20January%2019%202018.pdf">here</a>
          </label>
          <br/>
          <br/>
        </p>

        <div className="smaller" >
          Note: Your age expectancy shown below has been determined using standard actuarial mortality tables. You can override these entries.
        </div>
        <br/>

        <h3><i className="fa fa-fw fa-user-circle text-success" />&nbsp;Member</h3>

        <div className="col_2">
          <FormGroup>
            <MaterialInput placeholder="First Name"
              showValidityErrors={this.state.showErrors}
              value={member.firstName}
              onChange={(e) => this.updateMember('firstName', e.currentTarget.value)}
              minLength={1}
              trim
              required
            />
          </FormGroup>

          <FormGroup>
            <MaterialInput placeholder="Last Name"
              showValidityErrors={this.state.showErrors}
              value={member.lastName}
              onChange={(e) => this.updateMember('lastName', e.currentTarget.value)}
              trim
              required
            />
          </FormGroup>

          <FormGroup>
            <MaterialInput placeholder="Date of Birth" type="date"
              showValidityErrors={true}
              value={this.getMomentDateForInput(member.dob)}
              onChange={(e) => this.updateDateField(e, 'dob', this.updateMember.bind(this))}
              required={true}
            />
          </FormGroup>

          <FormGroup>
            <MaterialInput placeholder="Age Expectancy" type="number"
              showValidityErrors={true}
              value={member.selectedAgeExpectancy}
              onChange={(e) => this.updateMember('selectedAgeExpectancy', this.toNumber(e.currentTarget.value))}
              min={minAge} max={maxAge}
              required={true}
            />
          </FormGroup>
        </div >
        <div className="col_1">
          <FormGroup>
            <GenderSelector name="memberGender"
              gender={member.gender}
              onChange={(x) => {
                // tslint:disable-next-line:no-console
                console.log('Profile member gender update: ' + x);
                this.updateMember('gender', x)
            }} />
          </FormGroup>
        </div>

        <h3><i className="fa fa-fw fa-user-circle text-success" />&nbsp;Spouse</h3>
        <div className="col_2">
          <FormGroup>
            <MaterialInput placeholder="First Name"
              showValidityErrors={this.state.showErrors}
              value={spouse.firstName}
              onChange={(e) => this.updateSpouse('firstName', e.currentTarget.value)}
              minLength={1}
              trim
            />
          </FormGroup>

          <FormGroup>
            <MaterialInput placeholder="Last Name"
              showValidityErrors={this.state.showErrors}
              value={spouse.lastName}
              onChange={(e) => this.updateSpouse('lastName', e.currentTarget.value)}
              trim
            />
          </FormGroup>

          <FormGroup>
            <MaterialInput placeholder="Date of Birth" type="date"
              showValidityErrors={true}
              value={this.getMomentDateForInput(spouse.dob)}
              onChange={(e) => this.updateDateField(e, 'dob', this.updateSpouse.bind(this))}
              required={false}
            />
          </FormGroup>

          <FormGroup>
            <MaterialInput placeholder="Age Expectancy" type="number"
              showValidityErrors={true}
              value={spouse.selectedAgeExpectancy}
              min={minAge} max={maxAge}
              onChange={(e) => this.updateSpouse('selectedAgeExpectancy', this.toNumber(e.target.value))}
              required={spouseIsPresent}
            />
          </FormGroup>

        </div>
        <div className="col_1">
          <FormGroup>
            <GenderSelector name="spouseGender"
            gender={spouse.gender}
            onChange={(x) => {
              // tslint:disable-next-line:no-console
              console.log('Profile spouse gender update: ' + x);
              this.updateSpouse('gender', x)
            }} />
          </FormGroup>
        </div>
      </section >

      <footer>
        <Button type="submit" bsStyle="primary" bsSize="lg"
          disabled={!this.isFormValid()}
          onClick={() => this.setState({ showErrors: true })}>
          {this.state.btnText}
        </Button>
        <span className="profileError">{this.state.acceptancesError}</span>
      </footer>
    </form >
  }

  renderVideo(url: string, width: number, height: number) {
    const html5Markup =  <iframe src={url}
      style={{ position: 'absolute', width: '100%', height: '100%', left: 0 }}
      width={width} height={height}
      frameBorder={0}
      allowFullScreen />
    const IEMarkup = <object width={width} height={height} data={url}></object>
    return utils.isInternetExplorer() ? IEMarkup : html5Markup
  }

  render() {

    return (
      <section>
        {this.renderForm()}
      </section>
    )
  }
}

export {
  ProfileSettings
}



// WEBPACK FOOTER //
// ./src/app/containers/ProfileSettings/index.tsx